import React, { useEffect } from 'react'
import clsx from 'clsx'
import Section, { SectionBody, SectionBodyText } from '../../components/Section'
import BackToTop from '../../components/BackToTop'
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeaderCell,
    DataTableRow,
} from '../../components/DataTable'
import { tattoos, TattooEntry } from '../../data/tattoos'
import { setMetaTags } from '../../utils/metaTags'

interface TattooProps {
    theme: string
}

interface TattooPhoto {
    src?: string
    images?: string[]
    artist?: string
    flag?: string
    isMini?: boolean
}

const tattooPhotos: TattooPhoto[] = [
    {
        images: ['/assets/tattoo/dan-santoro-1.webp', '/assets/tattoo/tattoo-1.webp'],
        artist: 'Dan Santoro',
        flag: '🇺🇸',
        isMini: true,
    },
    { src: '/assets/tattoo/tattoo-2.webp', artist: 'Mike Elmo', flag: '🇺🇸' },
    { src: '/assets/tattoo/tattoo-3.webp', artist: 'Carlos Chavarraga', flag: '🇺🇸🇨🇴' },
    { src: '/assets/tattoo/hari-seda.webp', artist: 'Hari Seda', flag: '🇵🇷' },
]

function getYearLabel(entry: TattooEntry): string {
    if (!entry.date) return 'TBD'
    return entry.date.substring(0, 4)
}

function TattooPhotoCaption({ artist, flag }: { artist: string, flag?: string }) {
    return (
        <p className="mt-2 text-white">
            {artist}{flag ? ` ${flag}` : ''}
        </p>
    )
}

function TattooMiniCarousel({ images, artist, flag }: { images: string[], artist?: string, flag?: string }) {
    return (
        <div className="flex-shrink-0 w-[42vw] sm:w-[36vw] md:w-[14rem] snap-start">
            <div className="relative aspect-[4/5] bg-neutral-800 rounded-lg overflow-hidden">
                <div className="flex h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide">
                    {images.map((image, index) => (
                        <img
                            key={index}
                            src={image}
                            alt={artist ? `${artist} ${index + 1}` : `Tattoo photograph ${index + 1}`}
                            className="w-full h-full flex-shrink-0 snap-start object-cover"
                            loading="lazy"
                        />
                    ))}
                </div>
            </div>
            {artist && <TattooPhotoCaption artist={artist} flag={flag} />}
        </div>
    )
}

function TattooPhotoSlide({ photo }: { photo: TattooPhoto }) {
    if (photo.isMini && photo.images) {
        return (
            <TattooMiniCarousel
                images={photo.images}
                artist={photo.artist}
                flag={photo.flag}
            />
        )
    }

    const alt = photo.artist || 'Tattoo photograph'

    return (
        <div className="flex-shrink-0 w-[78vw] sm:w-[65vw] md:w-[22rem] snap-start">
            <div className="relative aspect-[4/5] bg-neutral-800 rounded-lg overflow-hidden">
                <img
                    src={photo.src}
                    alt={alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                />
            </div>
            {photo.artist && <TattooPhotoCaption artist={photo.artist} flag={photo.flag} />}
        </div>
    )
}

const Tattoo: React.FC<TattooProps> = ({ theme }) => {
    useEffect(() => {
        setMetaTags({
            title: 'Tattoo',
            description: 'A chronological log of tattoos, artists, and studios.',
            url: window.location.href,
        })
    }, [])

    let lastYear = ''

    return (
        <div>
            <Section id="tattoo">
                <SectionBody>
                    <SectionBodyText>
                        A log of tattoos, artists, and studios I've collected over the years.
                    </SectionBodyText>

                    <div
                        className={clsx(
                            '-mx-4 overflow-x-auto scrollbar-hide',
                            'md:w-[calc(100vw-288px)]',
                        )}
                    >
                        <div className="flex w-max gap-3 md:gap-4 pt-2 pb-4 snap-x snap-mandatory pl-4 pr-4 md:pr-8">
                            {tattooPhotos.map((photo, index) => (
                                <TattooPhotoSlide key={index} photo={photo} />
                            ))}
                        </div>
                    </div>

                    <DataTable
                        columnWidths={[
                            { className: 'w-[2.75rem] md:w-[3.5rem]' }, // Year — fixed
                            undefined, // Artist — fills
                            undefined, // Instagram — fills
                            { className: 'w-0 md:w-[28%]' }, // Location — desktop only
                        ]}
                    >
                        <DataTableHead>
                            <DataTableHeaderCell className="pl-3 pr-1">Year</DataTableHeaderCell>
                            <DataTableHeaderCell>Artist</DataTableHeaderCell>
                            <DataTableHeaderCell className="pr-3 md:pr-2">Instagram</DataTableHeaderCell>
                            <DataTableHeaderCell hiddenOnMobile className="md:pr-3">Location</DataTableHeaderCell>
                        </DataTableHead>
                        <DataTableBody>
                            {tattoos.map((entry, index) => {
                                const year = getYearLabel(entry)
                                const showYear = year !== lastYear
                                lastYear = year

                                return (
                                    <DataTableRow key={index}>
                                        <DataTableCell round="left" className="pr-1 text-white">
                                            {showYear ? year : ''}
                                        </DataTableCell>
                                        <DataTableCell truncate round="none" className="text-white">
                                            <span className="truncate block">{entry.artist}</span>
                                        </DataTableCell>
                                        {/* Location is hidden on mobile, so Instagram is the right edge there */}
                                        <DataTableCell
                                            truncate
                                            round="none"
                                            className="rounded-r-xl pr-3 md:rounded-none md:pr-2"
                                        >
                                            {entry.instagram ? (
                                                <a
                                                    href={`https://www.instagram.com/${entry.instagram}/`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-lime-500 hover:text-lime-400 transition-colors truncate block"
                                                >
                                                    @{entry.instagram}
                                                </a>
                                            ) : (
                                                <span className="text-gray-600">-</span>
                                            )}
                                        </DataTableCell>
                                        <DataTableCell
                                            truncate
                                            hiddenOnMobile
                                            round="none"
                                            className="text-white md:rounded-r-xl md:pr-3"
                                        >
                                            <span className="truncate block">
                                                {entry.location || <span className="text-gray-600">-</span>}
                                            </span>
                                        </DataTableCell>
                                    </DataTableRow>
                                )
                            })}
                        </DataTableBody>
                    </DataTable>
                </SectionBody>
            </Section>

            <BackToTop theme={theme} />
        </div>
    )
}

export default Tattoo
